/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Accordion, Col, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { SFCAccordion } from '../../../../components/accordion/SFCAccordion';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../components/generic';
import { SimpleTable } from '../../../../components/tables/SimpleTable';
import { useToast } from '../../../../context/ToastContext';
import { useGetParentQuery } from '../../../../services/endpoints/people/parent';
import {
  useGetAdminQuery,
  useGetInstructorQuery,
  useMimicMutation,
  User,
  UserType
} from '../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../services/helper';
import {
  childTableColumns,
  ordersTableColumns
} from '../../../../utils/consts/columnDefs/parentColumns';
import { userCanEditCreateDelete_AdminUser, userCanLoginAs } from './AdminsView';
import { userCanEditCreateDelete_Instructors } from './InstructorsView';
import { userCanEditCreateDelete_ParentUser } from './ParentsView';

import { Form as FormikForm, Formik, FormikErrors, FormikHelpers, FormikProps, FormikTouched } from 'formik';
import * as Yup from 'yup';
import { LinkCell } from '../../../../components/tables/cellComponents/LinkCell';
import { useCreateOrderMutation } from '../../../../services/endpoints/orders/orders';
import { revisionsColumns } from '../../../../utils/consts/columnDefs/sessionColumns';
import { getErrorMessage } from '../../../../utils/utils';
import { FormValues, OrdersForm } from './OrdersForm';
import { Forbidden } from '../../../../components/error/Forbidden';

const LabelTextList = ({ label, list }: { label: string; list: string[] }) => (
    <Stack
        direction="vertical"
        gap={1}
    >
    <span
        className="fw-semibold"
        style={{ color: 'rgba(94, 100, 105, .5)' }}
    >
      {label}
    </span>
      {list.map((text, index) => (
          <span
              key={index}
              className="text-secondary fw-semibold"
          >
        {text}
      </span>
      ))}
    </Stack>
);

const userLinksMap = (user: UserType, id?: number | string) => {
  if (user === 'customer') {
    return {
      new: `/admin/sfc-users/parents/new`,
      edit: `/admin/sfc-users/parents/${id}/edit`,
      back: `/admin/sfc-users/parents`
    };
  }
  if (user === 'instructor') {
    return {
      new: `/admin/dash-users/instructors/new`,
      edit: `/admin/dash-users/instructors/${id}/edit`,
      back: `/admin/dash-users/instructors`
    };
  } else {
    return {
      new: `/admin/dash-users/admins/new`,
      edit: `/admin/dash-users/admins/${id}/edit`,
      back: `/admin/dash-users/admins`
    };
  }
};

interface UserDetailsViewProps {
  userType: UserType;
}

export const UserDetailsView: React.FC<UserDetailsViewProps> = ({ userType }) => {
  const currentUser = getCurrentUser();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefetchedParent = (location.state as { parent?: User } | null)?.parent;
  const userID = +params.id!;
  const isOwnProfile = userID === Number(currentUser?.id);
  // Every restriction here is instructor-only. Admins are never blocked, and an instructor
  // always sees their own profile in full.
  const restrictForInstructor = !!currentUser?.isInstructor && !isOwnProfile;
  const canCreateOrder = !currentUser?.isInstructor;   // New Order is admin-only
  const canSeeOrdersTable = !restrictForInstructor;    // admins always; instructors only on their own
  const showFullDetails = !restrictForInstructor;      // others' Details trim to name/address/email for instructors
  const {addToast} = useToast();
  const [mimic] = useMimicMutation();
  const [currentStep, setCurrentStep] = useState(0);
  const [checkedValues, setCheckedValues] = useState<string[]>([]);
  const [createOrder] = useCreateOrderMutation();

  const [initialValues, setInitialValues] = useState<FormValues>({
    user_id: { label: '' },
    scholarship_id: { label: '' },
    course_id: { label: '' },
    child_ids: []
  });

  const validationSchema = Yup.object({
    scholarship_id: Yup.object({
      value: Yup.number().required('Scholarship selection is required'),
      label: Yup.string().optional()
    }).required(),
    course_id: Yup.object({
      value: Yup.number().required('Course selection is required'),
      label: Yup.string().optional()
    }).required()
  });

  const {
    data: adminData,
    error: adminError,
    isLoading: loadingAdmin
  } = useGetAdminQuery(userID, {
    skip: userType === 'instructor' || userType === 'customer'
  });
  const {
    data: instructorData,
    error: instructorError,
    isLoading: loadingInstructor
  } = useGetInstructorQuery(userID, {
    skip: userType !== 'instructor'
  });
  const {
    data: parentData,
    error: parentError,
    isLoading: loadingParent
  } = useGetParentQuery(userID, {
    skip: userType !== 'customer'
  });

  const [user, setUser] = useState<User>();

  useEffect(() => {
    if (adminError && !loadingAdmin) {
      addToast('Error while loading admin', 'error');
    }
  }, [adminData, adminError, loadingAdmin]);

  useEffect(() => {
    if (instructorError && !loadingInstructor) {
      addToast('Error while loading instructor', 'error');
    }
  }, [instructorData, instructorError, loadingInstructor]);

  useEffect(() => {
    if (
        parentError &&
        !loadingParent &&
        !('status' in parentError && parentError.status === 403)
    ) {
      addToast('Error while loading parent', 'error');
    }
  }, [parentData, parentError, loadingParent]);

  useEffect(() => {
    if (adminData) {
      setUser({
        ...adminData.admin,
        id: userID
      });
    }

    if (instructorData) {
      setUser({
        ...instructorData.instructor,
        id: userID
      });
    }

    if (parentData) {
      setUser({
        ...parentData.parent,
        id: userID
      });
    } else if (prefetchedParent && prefetchedParent.id === userID) {
      setUser({
        ...prefetchedParent,
        id: userID
      });
    }
  }, [adminData, instructorData, parentData, prefetchedParent, userID]);

  const steps = [{ name: 'Orders', form: OrdersForm }];

  const handleSumbit = async (
    formValue: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) => {
    if (checkedValues.length === 0) {
      addToast('Please select at least one child', 'error');
      return;
    }

    try {
      const submission: any = {}; //@TODO: set the correct type.
      submission.parent_id = user?.id as number;
      submission.scholarship_id = formValue.scholarship_id?.value ?? 0;
      submission.course_id = (formValue.course_id?.value ?? 0) as number;
      submission.child_ids = checkedValues.map(Number);

      const res = await createOrder(submission).unwrap();
      if (res && res.order) {
        addToast('Order created succesfully', 'success');
        resetForm();
        setCheckedValues([]);
      } else {
        addToast(getErrorMessage(res), 'error');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = event.target;

    let checkValues = [...checkedValues];

    if (checked) {
      if (!checkValues.includes(id)) {
        // Prevent duplicate entries
        checkValues.push(id);
      }
    } else {
      checkValues = checkValues.filter((value) => value !== id);
    }

    setCheckedValues(checkValues);
  };

  const handleNext = async (
      validateForm: () => Promise<FormikErrors<FormValues>>,
      setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
      values: FormValues
  ) => {
    const errors = await validateForm();

    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        user_id: { value: true },
        scholarship_id: { value: true }
      });
    }
  };
  if (loadingAdmin || loadingInstructor || (loadingParent && !prefetchedParent)) {
    return (
        <div className="w-100 vh-100 d-inline-flex">
          <Spinner
              variant="primary"
              className="m-auto"
          />
        </div>
    );
  }

  const accordion = (
      <SFCAccordion defaultActiveKey={['0', '1', '2']}>
        <Accordion.Item eventKey="0">
          <Accordion.Header>Children</Accordion.Header>
          <Accordion.Body>
            <SimpleTable
                columns={childTableColumns}
                data={user?.children ?? []}
            />
          </Accordion.Body>
        </Accordion.Item>
        {(canSeeOrdersTable || canCreateOrder) && (
        <Accordion.Item eventKey="1">
          <Accordion.Header>Orders</Accordion.Header>
          <Accordion.Body>
            {canSeeOrdersTable && (
                <SimpleTable
                    columns={ordersTableColumns}
                    data={[...(user?.orders ?? [])].sort(
                      (a, b) =>
                        new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
                    )}
                />
            )}

            {canCreateOrder && (
                <Formik
                    initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSumbit}
                enableReinitialize
            >
              {({
                  submitForm,
                  isSubmitting,
                  validateForm,
                  touched,
                  errors,
                  setTouched,
                  values,
                  setFieldValue
                }: FormikProps<FormValues>) => (
                  <FormikForm className="text-start">
                    <div>
                      {currentStep < steps.length &&
                          React.createElement(steps[currentStep]?.form, {
                            errors,
                            touched,
                            values,
                            setFieldValue
                          })}
                    </div>

                    <Form.Group
                        className="mb-lg-3 mb-1"
                        controlId="child"
                    >
                      {user?.children &&
                      Array.isArray(user.children) &&
                      user.children.length > 0 ? (
                          user.children.map((child, idx) => (
                              <Form.Check
                                  key={idx}
                                  type="checkbox"
                                  id={String(child?.id)}
                                  label={child.full_name}
                                  checked={checkedValues.includes(String(child?.id))}
                                  onChange={handleCheckboxChange}
                              />
                          ))
                      ) : (
                          <Form.Text className="text-muted">
                            No children found for this user. A child must be associated with this user to create an order.
                          </Form.Text>
                      )}
                    </Form.Group>
                    <div className="d-flex justify-content-end flex-wrap gap-2 mt-3">
                      {currentStep !== 0 && (
                        <SimpleButton
                          disabled={isSubmitting}
                          type="button"
                          onClick={() => setCurrentStep(currentStep - 1)}
                          className="px-4"
                          variant="outline-primary"
                          truncate={false}
                        >
                          Back
                        </SimpleButton>
                      )}

                      <SimpleButton
                        disabled={isSubmitting || checkedValues.length === 0}
                        type="button"
                        onClick={
                          currentStep === steps.length - 1
                            ? submitForm
                            : () => handleNext(validateForm, setTouched, values)
                        }
                        className="px-4"
                        variant="primary"
                        truncate={false}
                      >
                        {currentStep === steps.length - 1 ? 'Create Order' : 'Continue'}
                      </SimpleButton>
                    </div>
                  </FormikForm>
              )}
                </Formik>
            )}
          </Accordion.Body>
        </Accordion.Item>
        )}
        {!currentUser?.isInstructor && (
            <Accordion.Item eventKey="2">
          <Accordion.Header>Revisions</Accordion.Header>
          <Accordion.Body>
            <SimpleTable
                columns={revisionsColumns.filter((column) => column.header !== 'Comment')}
                data={user?.audits ?? []}
            />
          </Accordion.Body>
        </Accordion.Item>
            )}
      </SFCAccordion>
  );

  const userDetails = (
      <>
        <DetailSection title="Details">
          {showFullDetails && <LabelText label="ID:">{user?.id}</LabelText>}
          {showFullDetails && (
              <LabelText
                  label="Address:"
                  direction="vertical"
              >
                {user?.address}
                {user?.address2 ? `, ${user?.address2}` : ''}
                <br />
                {user?.city}
                {user?.state ? `, ${user?.state}` : ''}
                {user?.zip ? `, ${user?.zip}` : ''}
              </LabelText>
          )}
          <LabelText label="Email:">{user?.email}</LabelText>
          {showFullDetails && <LabelText label="Phone:">{user?.phone}</LabelText>}

          {userType !== 'customer' && showFullDetails && (
              <>
                <hr />
                <LabelText label="In-Person Rate:">
                  ${user?.instructor_session_rate?.toFixed(2)}
                </LabelText>
                <LabelText label="In-Person Reimbursement:">
                  ${user?.instructor_session_reimbursement?.toFixed(2)}
                </LabelText>
                <LabelText label="Online Rate:">${user?.instructor_online_rate?.toFixed(2)}</LabelText>
                <LabelText label="Online Reimbursement:">
                  ${user?.instructor_online_reimbursement?.toFixed(2)}
                </LabelText>
              </>
          )}
          {/* TODO: add refunds summary */}
          {/*<LabelText label="Total Refunds:">${user?.total_refunds?.toFixed(2)}</LabelText>*/}
        </DetailSection>

        {showFullDetails && (
            <DetailSection title="Scholarships">
          <LinkCell
              content={`${user?.discounts?.length} Discounts`}
              url={`/admin/orders/discounts?user_id=${user?.id}`}
              underline={true}
          />
          {user?.discounts && Array.isArray(user?.discounts) && user?.discounts?.length > 0 ? (
              user?.discounts.map((d, index) => (
                  <li key={index}>
                    <LinkCell
                        content={`${d.scholarship.name} - ${d.scholarship.percent_discount}%`}
                        url={`/admin/orders/discounts/${d.id}`}
                        underline={true}
                    />
                  </li>
              ))
          ) : (
              <span className="fst-italic">There are no contacts to show.</span>
          )}
        </DetailSection>
        )}

        {/* TODO: add videos list */}
        {/* <DetailSection title="Videos">
        {user?.courses.map((c) => c.venue.organizations.map((o) => (
          <LabelTextList label={`${o.name} Notes`} list={[o.notes]} />
        )))}
        Videos
      </DetailSection> */}
      </>
  );

  if (
      userType === 'customer' &&
      parentError &&
      'status' in parentError &&
      parentError.status === 403 &&
      !prefetchedParent
  ) {
    return <Forbidden />;
  }

  return (
      <>
        <div
            className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
            style={{ background: '#EFEFEF' }}
        >
          <Stack
              direction="horizontal"
              gap={2}
              className="ps-lg-3 ps-0"
          >
            <button
                className="btn btn-link text-info p-0"
                onClick={() => navigate(userLinksMap(userType, user?.id)?.back ?? '')}
            >
              <FaArrowLeft
                  size={20}
                  className="d-lg-none"
              />
              <FaArrowLeft
                  size={24}
                  className="d-none d-lg-block"
              />
            </button>
            <h2
                className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
                style={{ color: '#888888', fontSize: '1rem' }}
            >
              Dash Users
            </h2>
            <h2 className="d-none d-lg-block text-info fw-bold mb-0">
              {user?.first_name} {user?.last_name}
            </h2>
          </Stack>
          <Stack
              direction="horizontal"
              gap={1}
              className="d-flex"
          >
            {((userCanEditCreateDelete_Instructors(currentUser) && userType === 'instructor') ||
                (userCanEditCreateDelete_AdminUser(currentUser) && userType === 'admin') ||
                (userCanEditCreateDelete_ParentUser(currentUser) && userType === 'customer')) && (
                <SimpleButton
                    variant="primary"
                    height={36}
                    onClick={() => navigate(userLinksMap(userType, user?.id)?.edit ?? '')}
                >
                  Edit {userType}
                </SimpleButton>
            )}
            {userCanLoginAs(currentUser) && (
              <SimpleButton
                  variant="secondary"
                  height={36}
                  onClick={() => {
                    if (user?.id) {
                      mimic(user.id)
                          .unwrap()
                          .then((r) => {
                            window.location.reload();
                          });
                    } else {
                      console.error('user Id is undefined');
                    }
                  }}
              >
                Mimic
              </SimpleButton>
            )}
          </Stack>
        </div>
        <div className="d-lg-none">
          <Stack
              gap={4}
              className="p-4"
          >
            {userDetails}
            {accordion}
          </Stack>
        </div>
        <div className="d-none d-lg-flex">
          <div
              className="w-100 mt-5 px-5 py-4"
              style={{ maxWidth: 'calc(100% - 400px)' }}
          >
            {accordion}
          </div>
          <Stack
              gap={4}
              className="vh-100 p-4"
              style={{ width: 400, background: '#F2F2F2' }}
          >
            {userDetails}
          </Stack>
        </div>
      </>
  );
};